import { daysSince, todayISO } from "./utils";
import PRICING from "./pricing.json";

export const TURKISH_MIN = 1500;
export const MAX_WORDS = 3500;
// Actively offered in the base-language picker (Onboarding + Goals) AND the set we generate
// new AI translations for (see ACTIVELY_TRANSLATED_LANGS below, which reuses this array as its
// single source of truth). Order is deliberate — do not alphabetize or reorder.
//
// Arabic, Italian, Portuguese, Russian, Chinese, and Hindi were cut from this list on
// 2026-07-09 to reduce translation-generation burden. This does NOT delete their content:
// their static i18n bundles (ar.js/it.js/pt.js/ru.js/zh.js/hi.js) stay wired up in
// src/i18n/index.js, and any existing cached /translations/ or /ui_strings/ docs for them keep
// being read normally. A legacy profile with baseLanguage still set to one of these six will
// keep rendering correctly (RTL, cached content) — it just won't appear pre-selected in the
// picker grid, and no NEW translations will be generated for it going forward. They (plus
// Japanese, which was never in this list) are reachable again via the "Other language" picker
// (EXTRA_LANGS below) — they were accidentally left out of both lists for a couple weeks
// (2026-07-09 to 2026-07-25), making them unselectable anywhere, which was never the intent.
export const BASE_LANGS = ["English","Spanish","German","French","Turkish","Farsi"];
// Languages we actively generate new AI translations for. Deliberately just an alias of
// BASE_LANGS (not a second list) so the picker and the generation guard can never drift apart.
export const ACTIVELY_TRANSLATED_LANGS = BASE_LANGS;
export const EXTRA_LANGS = ["Afrikaans","Albanian","Amharic","Arabic","Armenian","Azerbaijani","Basque","Belarusian","Bengali","Bosnian","Bulgarian","Burmese","Catalan","Chinese","Croatian","Czech","Danish","Dutch","Estonian","Filipino","Finnish","Georgian","Greek","Gujarati","Hausa","Hebrew","Hindi","Hungarian","Icelandic","Indonesian","Irish","Italian","Japanese","Javanese","Kannada","Kazakh","Khmer","Korean","Kurdish","Kyrgyz","Lao","Latvian","Lithuanian","Macedonian","Malay","Malayalam","Maltese","Marathi","Mongolian","Nepali","Norwegian","Pashto","Polish","Portuguese","Punjabi","Romanian","Russian","Serbian","Sinhalese","Slovak","Slovenian","Somali","Swahili","Swedish","Tajik","Tamil","Telugu","Thai","Ukrainian","Urdu","Uzbek","Vietnamese","Welsh","Yoruba","Zulu"];

export const TARGET_LANGUAGES = [
  { code: "turkish", label: "Turkish" },
];

// Every language a partner teacher can be shown on their Teacher Dashboard as one they teach
// ("Languages I teach"), in display order. The whole list always renders, but only languages
// that are currently an accepted course language (TARGET_LANGUAGES) can actually be toggled on —
// the rest show disabled ("coming soon"), so the public teacher directory can never advertise a
// language Speafy doesn't teach yet. This is a deliberate superset of TARGET_LANGUAGES: the day
// a language graduates into TARGET_LANGUAGES it becomes selectable here automatically, no edit
// to this list needed.
export const TEACHABLE_LANGUAGES = ["English", "French", "Spanish", "German", "Farsi", "Turkish"];
// The subset a teacher may actually select right now — the labels of the live target languages.
export const SELECTABLE_TEACHABLE_LANGUAGES = TARGET_LANGUAGES.map((l) => l.label);

export const CLAUDE_SONNET_MODEL = "claude-sonnet-5";

export const TRIAL_DAYS = 7;
// Max lessons a learner can start in one calendar day (AllLessonsView's
// overwhelm gate). Named so Babi's app-knowledge reference (babiKnowledge.js)
// can quote the real value instead of a second hand-typed copy of "6".
export const DAILY_LESSON_LIMIT = 6;
// A6 (item 9): single source of truth for every price this app charges or displays —
// src/pricing.json. Plain JSON (not a .js module) specifically so a plain Node script outside
// the CRA/webpack build (scripts/inject-landing-pricing.js, run as a predeploy step for the
// landing/ Firebase Hosting target) can read the exact same values via a trivial
// JSON.parse(fs.readFileSync(...)) without any ESM/CJS interop with this file. These are
// DISPLAY-ONLY figures — Paddle's own dashboard-configured prices (referenced by the opaque
// PADDLE_PRICE_* env vars below) are the real billing source of truth; changing a value here
// does not change what Paddle actually charges, and the two must be kept in sync by hand
// whenever a price changes in Paddle. landing/pricing.html, landing/index.html, and
// landing/terms.html read this same file via that predeploy script — see its own comments for
// the marker-based find/replace mechanism. functions/adminActions.js's KPI dashboard has its own
// hardcoded copy (kept in sync by hand — that deploy boundary has no access to src/ at runtime,
// see that file's own comment).
export const PRICE_MONTHLY_USD       = PRICING.pro.monthly;
export const PRICE_ANNUAL_USD        = PRICING.pro.yearly;
export const ELITE_PRICE_MONTHLY_USD = PRICING.elite.monthly;
export const ELITE_PRICE_YEARLY_USD  = PRICING.elite.yearly;
export const VOICE_OVERAGE_PRICE_PER_HOUR_USD = PRICING.voiceOverage.perHour;
export const VOICE_OVERAGE_MAX_HOURS_PER_PURCHASE = PRICING.voiceOverage.maxHoursPerPurchase;

export const BADGES = [
  { min: 0,    icon: "🌱", key: "newbie" },
  { min: 500,  icon: "📖", key: "wordExplorer" },
  { min: 1000, icon: "🎯", key: "wordNerd" },
  { min: 1500, icon: "📚", key: "wordWizard" },
  { min: 2000, icon: "🎓", key: "rockstar" },
  { min: 2500, icon: "⚡", key: "expert" },
  { min: 3000, icon: "🏆", key: "champion" },
];

export function getBadge(deeplyRetained) {
  let b = BADGES[0];
  for (const x of BADGES) { if (deeplyRetained >= x.min) b = x; }
  return b;
}

// A "grandfathered" account: a legacy user from before the pre-subscription trial existed
// (profile.trialStartDate has been stamped by the onUserCreated Cloud Function since v1.6.0 /
// 2026-06-27, so a genuine grandfathered account — now 70+ days old — never has it) AND with no
// Paddle subscription on file at all. A permanent free-access class that has always been treated
// as Elite-equivalent — NOT a trial, and NOT a subscriber whose plan later lapsed.
// Shared by getVoiceLimitSeconds() (its Elite-minutes branch) and hasVoiceLabAccess() so the
// two read the exact same condition and can't drift apart.
//
// NOTE: isPremiumActive() below has a similar-looking `!profile?.trialStartDate` check but
// deliberately does NOT call this helper — see the comment there. That check is intentionally
// broader (it also keeps a legacy account premium after a later subscription is cancelled) and
// sits after an early `return`, so swapping in this stricter, no-subscription condition would
// change isPremiumActive's behaviour.
export function isGrandfathered(profile, subscription) {
  return !subscription?.status && !profile?.trialStartDate;
}

export function isPremiumActive(profile, subscription, isAdminAccount, isSimpleViewer) {
  // Admin accounts (support_viewer or full_admin custom claim) get unlimited
  // access in the consumer app too — display/gating override only, never
  // written to `subscription` itself. simple_viewer (friends doing informal
  // QA/bug-hunting, zero admin capability) get the same free-access bypass.
  if (isAdminAccount || isSimpleViewer) return true;
  // "trialing" is Paddle's own status for a subscription still in its (Paddle-billed) trial
  // period — confirmed live in production Firestore (backlog task 50) that this actually
  // occurs, not just a theoretical Paddle status. Treat it the same as "active": Paddle is
  // telling us this is a paying-track subscription, not this app's separate pre-subscription
  // trial (gated below by profile.trialStartDate).
  if (subscription?.status === "active" || subscription?.status === "trialing") return true;
  // Legacy free-access: no pre-app trial was ever started. Deliberately NOT the shared
  // isGrandfathered() helper — this is intentionally broader (a legacy account whose later
  // subscription lapsed still lands here and keeps access), and returning here rather than
  // falling through to the daysSince line also keeps a null `profile` from throwing below.
  if (!profile?.trialStartDate) return true; // grandfathered user: full access
  return daysSince(profile.trialStartDate) < TRIAL_DAYS;
}

export function trialDaysLeft(profile) {
  if (!profile?.trialStartDate) return TRIAL_DAYS;
  return Math.max(0, TRIAL_DAYS - daysSince(profile.trialStartDate));
}

// ─── User role (profile.role on /users/{uid}) ──────────────────────────────
// Server-managed, like trialStartDate: firestore.rules blocks the client from writing
// profile.role, and only the setUserRole Cloud Function (functions/adminActions.js) changes it.
// Absent on a document = "learner" — so no backfill is needed for existing accounts, and a new
// account needs no write. To add a future role: add it here and to VALID_USER_ROLES in
// adminActions.js; the Firestore field itself never needs a schema change.
// Deliberately INDEPENDENT of subscription tier / trial state — a teacher can be on any plan,
// and being on a paid plan never implies "teacher". Never conflate the two.
export const USER_ROLE_LEARNER = "learner";
export const USER_ROLE_TEACHER = "teacher";
export const VALID_USER_ROLES = [USER_ROLE_LEARNER, USER_ROLE_TEACHER];

export function getUserRole(profile) {
  return profile?.role === USER_ROLE_TEACHER ? USER_ROLE_TEACHER : USER_ROLE_LEARNER;
}

export function isTeacher(profile) {
  return getUserRole(profile) === USER_ROLE_TEACHER;
}

// ─── Teacher referral link ─────────────────────────────────────────────────
// The public URL a teacher shares to recruit students (shown on the Teacher Dashboard).
// The referral code IS the teacher's Firebase uid, on purpose — not a hashed/opaque token:
// the later referral-tracking pipeline (link click → cookie → Paddle custom_data → the
// subscription webhook) can resolve `?ref=` straight to a teacherId with no lookup table, and
// write profile.referredBy in the exact { teacherId, teacherDisplayName } shape
// linkStudentToTeacher already uses — one mechanism, not two. A uid is not a secret (it's
// already sent in Paddle custom_data from the client, and every Firestore read is gated by
// request.auth.uid regardless), so exposing it in a URL is safe.
// NOTE: the click-tracking / attribution behind this link does NOT exist yet — this only
// builds and displays the stable URL.
export const REFERRAL_LINK_BASE = "https://app.speafy.com";
export function buildReferralLink(teacherUid) {
  return teacherUid ? `${REFERRAL_LINK_BASE}/?ref=${encodeURIComponent(teacherUid)}` : "";
}

// A new signup's trialStartDate is stamped server-side by the onUserCreated Cloud Function
// (functions/index.js), never by the client. So for the first moments of a freshly created
// account — and longer if that function is delayed or fails — the client has no trialStartDate
// to read. Without a fallback, isPremiumActive / getVoiceLimitSeconds misread that account as
// "grandfathered" (a legacy pre-2026-06-27 account with permanent free access and Elite-level
// voice minutes) rather than "trial in progress" — which is exactly how a brand-new trial user
// ends up seeing "3h 0m" of voice minutes instead of the 10-minute trial allotment.
//
// Distinguish the two by account age: trialStartDate shipped in v1.6.0 (2026-06-27), so every
// genuine grandfathered account is now 70+ days old, while a signup whose CF write hasn't
// landed yet is minutes-to-days old. Treat a missing trialStartDate on an account younger than
// this window as a trial that began at account-creation time.
export const TRIAL_START_FALLBACK_MAX_AGE_DAYS = 30;

export function resolveTrialStartDate(rawTrialStartDate, authCreationTime) {
  if (rawTrialStartDate) return rawTrialStartDate;
  if (!authCreationTime) return null;
  return daysSince(authCreationTime) <= TRIAL_START_FALLBACK_MAX_AGE_DAYS
    ? new Date(authCreationTime).toISOString()
    : null;
}

// Bumping this forces every user to see (and re-accept) Babi's voice consent
// screen again, even if profile.voiceConsent.accepted is already true — the
// mechanism for invalidating consent once given under stale/placeholder copy
// after the copy itself changes. Shared between AgentChat.js (consent screen +
// mic icon) and SettingsView.js (Your Data toggle) — both must read the exact
// same version to avoid drifting out of sync with each other.
export const VOICE_CONSENT_VERSION = "v1";

export function hasValidVoiceConsent(profile) {
  const c = profile?.voiceConsent;
  return !!(c?.accepted && c.version === VOICE_CONSENT_VERSION);
}

// A5: voice-minutes balance. There's only one paid tier today, so this is just
// isPremiumActive() under a voice-specific name — kept distinct rather than calling
// isPremiumActive() directly at voice call sites so an Elite-specific carve-out (A6)
// has one place to land later without touching every caller.
export function hasVoiceEntitlement(profile, subscription, isAdminAccount, isSimpleViewer) {
  return isPremiumActive(profile, subscription, isAdminAccount, isSimpleViewer);
}

// Basic tier's monthly voice-minutes allotment (30 min), per the master plan.
export const VOICE_BASIC_LIMIT_SECONDS = 30 * 60;
// Elite tier's monthly voice-minutes allotment (3 hours), per the master plan.
export const VOICE_ELITE_LIMIT_SECONDS = 3 * 60 * 60;
// A6: trial-period voice allotment — deliberately tier-agnostic and smaller than even Basic's
// own limit, confirmed as a deliberate acquisition-cost decision, not a placeholder. Applies to
// anyone actively within their trialStartDate grace window (no real Paddle subscription yet),
// regardless of which tier they'll eventually pick.
export const VOICE_TRIAL_LIMIT_SECONDS = 10 * 60;
// Internal simple_viewer accounts (friends doing informal QA) get the same modest allotment as a
// paid Basic/Pro subscriber — enough to exercise voice features, not a free Elite equivalent.
// full_admin / support_viewer keep the Elite 3h (they may need to reproduce a paying Elite
// user's exact experience).
export const VOICE_SIMPLE_VIEWER_LIMIT_SECONDS = VOICE_BASIC_LIMIT_SECONDS; // 30 min

export const VOICE_TIER_BASIC = "basic";
export const VOICE_TIER_ELITE = "elite";

// Overage minutes are sold in fixed one-hour blocks, not a finer-grained per-minute rate —
// matches Paddle's one-time-price model (one priceId = one purchasable unit) with no extra
// quantity-selection UI needed. Revisit if a different unit is ever wanted.
export const VOICE_OVERAGE_SECONDS_PER_UNIT = 60 * 60;

// A6: below this remaining balance, an Elite subscriber sees the "buy more minutes" option.
// Deliberately much larger and calmer than VOICE_LOW_BALANCE_BUFFER_SECONDS (75s) below, which
// is about Babi's own end-of-conversation wrap-up behavior, not purchasing — the two checks are
// independent and can both be true near the very end of a conversation.
export const VOICE_OVERAGE_OFFER_THRESHOLD_SECONDS = 30 * 60;

// Reads the active subscription's mirrored tier field (set by the Paddle webhook once it can
// tell Basic and Elite price IDs apart — see subscriptionWebhook.js). Defaults to Basic for any
// subscription that predates this field (real Paddle subscriptions from before A6 shipped).
export function getVoiceTier(subscription) {
  return subscription?.tier === VOICE_TIER_ELITE ? VOICE_TIER_ELITE : VOICE_TIER_BASIC;
}

// Deliberately never persisted to Firestore — always derived fresh from entitlement/tier, so
// a tier/entitlement change is reflected immediately rather than needing a stored value
// to be separately kept in sync. Confirmed (A6): a mid-cycle Basic->Elite upgrade takes effect
// immediately with no proration of minutes, a natural consequence of this being derived fresh
// rather than stored — the very next read already reflects the new tier's full limit.
export function getVoiceLimitSeconds(profile, subscription, isAdminAccount, isSimpleViewer) {
  if (!hasVoiceEntitlement(profile, subscription, isAdminAccount, isSimpleViewer)) return 0;
  if (isAdminAccount) return VOICE_ELITE_LIMIT_SECONDS;          // full_admin + support_viewer
  if (isSimpleViewer) return VOICE_SIMPLE_VIEWER_LIMIT_SECONDS;  // 30m — informal-QA accounts
  if (!subscription?.status) {
    // No real Paddle subscription on file at all — two genuinely different cases, previously
    // (wrongly) conflated into one "give them Elite's limit" branch:
    // - Actively trialing (profile.trialStartDate set, still within TRIAL_DAYS): confirmed via
    //   hasVoiceEntitlement/isPremiumActive already having passed above — if the trial had
    //   expired, entitlement would already be false and execution would never reach here. Gets
    //   the deliberately small, tier-agnostic trial allotment, confirmed 2026-08-17.
    // - Grandfathered (no trialStartDate field at all — a legacy account predating it): not a
    //   trial in any real sense, a permanent free-access class — keeps the prior generous
    //   Elite-level default, unchanged from A6's original build.
    // Inside this `!subscription?.status` block, isGrandfathered() reduces to exactly
    // `!profile?.trialStartDate`, so this stays byte-for-byte equivalent to the previous
    // `profile?.trialStartDate ? TRIAL : ELITE` — it just names the concept and shares it.
    return isGrandfathered(profile, subscription) ? VOICE_ELITE_LIMIT_SECONDS : VOICE_TRIAL_LIMIT_SECONDS;
  }
  return getVoiceTier(subscription) === VOICE_TIER_ELITE ? VOICE_ELITE_LIMIT_SECONDS : VOICE_BASIC_LIMIT_SECONDS;
}

// The single "does this account have Elite-level voice access" check — a real Elite subscription,
// or an admin/simple_viewer treated as Elite-equivalent (matching getVoiceLimitSeconds above).
// Deliberately does NOT include trial users: a trial has no tier. Callers that also want to admit
// trial users (e.g. the Listen & Speak Lab gate) OR this with isOnVoiceTrial() themselves.
export function hasEliteEntitlement(profile, subscription, isAdminAccount, isSimpleViewer) {
  if (isAdminAccount || isSimpleViewer) return true;
  if (!hasVoiceEntitlement(profile, subscription, isAdminAccount, isSimpleViewer)) return false;
  return getVoiceTier(subscription) === VOICE_TIER_ELITE;
}

// A6: overage-minutes purchase is Elite-only — a Basic subscriber has no top-up path at all.
// Kept as a thin alias of hasEliteEntitlement so existing call sites don't need to change and
// the "is this an Elite account" logic lives in exactly one place.
export function canBuyVoiceOverage(profile, subscription, isAdminAccount, isSimpleViewer) {
  return hasEliteEntitlement(profile, subscription, isAdminAccount, isSimpleViewer);
}

// Someone actively inside their pre-subscription trial window (no real Paddle subscription yet).
// There is only ONE trial — it is tier-agnostic, not a separate Pro/Elite trial. Shared by
// SettingsView's voice-minutes label and the Listen & Speak Lab access gate so the two can't
// drift apart.
export function isOnVoiceTrial(profile, subscription, isAdminAccount, isSimpleViewer) {
  return !subscription?.status && !isAdminAccount && !isSimpleViewer
    && !!profile?.trialStartDate && trialDaysLeft(profile) > 0;
}

// Listen & Speak Lab is an Elite-tier feature — plus trial users (so the trial can show it off),
// admin/simple_viewer, and grandfathered-free accounts (which already hold the Elite-level
// voice-minute allotment — see getVoiceLimitSeconds). Only a Basic/Pro subscriber does NOT
// qualify and is routed to the upgrade screen. Everyone who DOES qualify draws from the same
// shared profile.voiceBalance pool — the trial's VOICE_TRIAL_LIMIT_SECONDS allowance is
// unchanged, there is no separate Lab budget.
export function hasVoiceLabAccess(profile, subscription, isAdminAccount, isSimpleViewer) {
  return hasEliteEntitlement(profile, subscription, isAdminAccount, isSimpleViewer)
    || isOnVoiceTrial(profile, subscription, isAdminAccount, isSimpleViewer)
    || isGrandfathered(profile, subscription);
}

// A6: the "get more minutes" affordance's actual visibility gate — Elite-only AND under the
// 30-minute offer threshold. Independent of VOICE_LOW_BALANCE_BUFFER_SECONDS's wrap-up trigger.
export function shouldOfferVoiceOverage(remainingSeconds, profile, subscription, isAdminAccount, isSimpleViewer) {
  return canBuyVoiceOverage(profile, subscription, isAdminAccount, isSimpleViewer)
    && remainingSeconds < VOICE_OVERAGE_OFFER_THRESHOLD_SECONDS;
}

// Within the master plan's "last 60-90 seconds" buffer-zone window — the point at which
// Babi is instructed to gracefully wrap up a voice conversation via wrap_up_conversation.
export const VOICE_LOW_BALANCE_BUFFER_SECONDS = 75;

// Rollover-aware read of the current cycle's used seconds. Does NOT persist anything —
// callers about to WRITE a change should merge this resolved state into their update
// rather than trusting profile.voiceBalance's raw stored usedSeconds directly, which may
// already belong to a billing cycle that's since rolled over.
//
// Anchored to subscription.currentPeriodEnd (Paddle updates this to the *next* cycle's
// end on every renewal, so a changed value reliably signals a new cycle has begun) rather
// than calendar-month math, since that's the more correct "resets exactly when the user
// is actually billed" behavior — EXCEPT for a user who is premium via the trial-days
// grandfather path in isPremiumActive() with no real Paddle subscription on file at all,
// where there's no currentPeriodEnd to anchor to. That case falls back to a simple
// calendar-month comparison instead (reusing todayISO()'s date-string style, matching how
// the rest of this app already does simple date math, e.g. the streak logic) — UNLESS a
// live subscription (status "active"/"trialing") already exists but its currentPeriodEnd
// hasn't been written yet, the optimistic checkout.completed window, which is treated as a
// fresh cycle (usedSeconds 0) so pre-upgrade usage isn't shown against the new plan.
//
// A6: this anchor mismatch is ALSO what makes a trial-to-paid conversion reset usedSeconds to
// 0 rather than carry it over — confirmed and approved 2026-08-17, not an accident. Before
// conversion, stored.cycleAnchor is a "trial:…" string (the trial branch below); the instant a
// real subscription exists, subscription.currentPeriodEnd is a full ISO timestamp, which can
// never string-equal that, so sameCycle is guaranteed false and the balance starts fresh
// against the new tier's full limit. The optimistic-window carve-out above makes that reset
// happen immediately on upgrade instead of only after the webhook lands / the app reloads.
// This is deliberately the OPPOSITE of the Basic<->Elite tier-change case (which keeps the
// same real currentPeriodEnd throughout, so it correctly carries usedSeconds over instead) —
// do not "fix" this into carrying trial usage forward.
//
// TRIAL: the pre-subscription trial is a fixed 7-day window, not a monthly cycle, so its usage
// is anchored to trialStartDate (one pool, no mid-trial reset) — NOT calendar-month math.
//
// YEARLY PLANS: subscription.currentPeriodEnd is the ANNUAL renewal date (~12 months out) and
// only changes once a year, so anchoring straight to it would hand a yearly subscriber their
// whole year's voice allowance as a single bucket. The voice allotments (Elite 3h, Basic 30m)
// are defined PER MONTH, so for a yearly plan we anchor to the current *monthly* sub-period
// instead — see monthlySubPeriodAnchor below.
//
// PURCHASED POOL (Elite overage): purchasedSeconds is a SEPARATE, PERSISTENT balance — the
// *remaining* purchased seconds, not a lifetime total. This function returns it untouched in
// every branch, so it never resets on any rollover (monthly, yearly-monthly, trial→paid, tier
// change, cancellation). It goes UP only when the webhook credits a purchase, and DOWN only in
// decrementVoiceBalance (useVoiceTurn.js), and only for usage past the current cycle's monthly
// limit — the monthly allowance is always spent first. So a bought pack carries forward until
// used and is never "refilled" alongside the monthly reset.

// The start of the monthly sub-period that `now` currently falls in, for a plan whose Paddle
// billing period is annual. `billingDay` is taken from the annual renewal date's day-of-month
// (the same day Paddle renews a monthly plan on); months without that day (e.g. the 31st in
// February) clamp to the month's last day, matching Paddle's own monthly-billing behaviour.
// Returns a full ISO timestamp so it shares the exact string shape of a real currentPeriodEnd
// (no new anchor format to reason about) and is deterministic within a given month.
function monthlySubPeriodAnchor(annualPeriodEndISO, now = new Date()) {
  const end = new Date(annualPeriodEndISO);
  if (isNaN(end.getTime())) return annualPeriodEndISO; // unparseable — leave the raw value be
  const billingDay = end.getUTCDate();
  const dayFor = (y, m) => Math.min(billingDay, new Date(Date.UTC(y, m + 1, 0)).getUTCDate());
  let y = now.getUTCFullYear();
  let m = now.getUTCMonth();
  let start = new Date(Date.UTC(y, m, dayFor(y, m)));
  if (start.getTime() > now.getTime()) {
    // This month's billing day hasn't arrived yet — the active period began last month.
    m -= 1;
    if (m < 0) { m = 11; y -= 1; }
    start = new Date(Date.UTC(y, m, dayFor(y, m)));
  }
  return start.toISOString();
}

export function resolveVoiceBalance(profile, subscription) {
  const stored = profile?.voiceBalance || {};
  const purchasedSeconds = stored.purchasedSeconds || 0;
  let currentAnchor = subscription?.currentPeriodEnd || null;
  // Yearly plan: step the reset cadence down from annual to monthly (see comment above).
  if (currentAnchor && subscription?.planType === "yearly") {
    currentAnchor = monthlySubPeriodAnchor(currentAnchor);
  }

  if (!currentAnchor) {
    // A live paid subscription whose currentPeriodEnd hasn't landed yet — the optimistic
    // window between Paddle's checkout.completed event (App.js sets status:"active" alone)
    // and the subscription webhook writing the real billing period. A brand-new paid cycle
    // has just begun, so this cycle's usage starts at zero. Without this, a free/trial user
    // who upgrades mid-month keeps seeing their pre-upgrade seconds counted against the new
    // plan's limit ("1m 16s used of 30m") until the webhook arrives / the app reloads —
    // the anchor-mismatch reset described above only fires once a real currentPeriodEnd
    // exists to mismatch against, and the subscription isn't live-synced during a session.
    if (subscription?.status === "active" || subscription?.status === "trialing") {
      return { usedSeconds: 0, cycleAnchor: todayISO(), purchasedSeconds };
    }
    // Pre-subscription trial: a fixed 7-day window, NOT a monthly cycle. Anchor the whole
    // trial's usage to trialStartDate so the 10-minute allotment is one pool that never
    // resets mid-trial — a trial that straddles a calendar-month boundary must not hand out
    // a second allotment on the 1st. On trial→paid conversion the anchor becomes a real
    // timestamp (the branch below), which can never string-equal "trial:…", so usage
    // correctly resets to 0 for the first paid cycle. (trialStartDate may be an ISO string
    // or a Firestore-timestamp-shaped object — normalise to a stable scalar either way.)
    if (profile?.trialStartDate != null) {
      const rawStart = profile.trialStartDate?.seconds ?? profile.trialStartDate;
      const trialAnchor = `trial:${rawStart}`;
      const sameTrial = stored.cycleAnchor === trialAnchor;
      return {
        usedSeconds: sameTrial ? (stored.usedSeconds || 0) : 0,
        cycleAnchor: trialAnchor,
        purchasedSeconds,
      };
    }
    // Grandfathered free account (no trialStartDate, no Paddle subscription): keeps the
    // simple calendar-month reset for its Elite-level default allotment.
    const thisMonth = todayISO().slice(0, 7); // "YYYY-MM"
    const storedMonth = (stored.cycleAnchor || "").slice(0, 7);
    const sameMonth = !!stored.cycleAnchor && storedMonth === thisMonth;
    return {
      usedSeconds: sameMonth ? (stored.usedSeconds || 0) : 0,
      cycleAnchor: sameMonth ? stored.cycleAnchor : todayISO(),
      purchasedSeconds,
    };
  }

  const sameCycle = stored.cycleAnchor === currentAnchor;
  return {
    usedSeconds: sameCycle ? (stored.usedSeconds || 0) : 0,
    cycleAnchor: currentAnchor,
    purchasedSeconds,
  };
}
